import React, { useCallback, useEffect, useState } from "react";
import { Bell, BellOff, CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  isPushSupported,
  isStandalonePWA,
  subscribe,
  unsubscribe,
  currentSubscription,
} from "@/lib/push";

/**
 * Push notification settings card.
 *
 * Renders one of five states:
 *   1. Loading — checking browser/subscription state
 *   2. Unsupported — browser can't do push (older Safari, etc.)
 *   3. iOS-needs-install — iPhone user hasn't added to home screen yet
 *   4. Not subscribed — big Enable button
 *   5. Subscribed — shows count + "Send test" and "Disable" actions
 */
export default function PushSettingsCard() {
  const [supported] = useState(isPushSupported);
  const [standalone] = useState(isStandalonePWA);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [count, setCount] = useState(0);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [sub, { data }] = await Promise.all([
        currentSubscription(),
        api.get("/push/status"),
      ]);
      setEnabled(!!sub);
      setCount(data.subscription_count || 0);
    } catch {
      // ignore — the card will show "not enabled" as default
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (supported) refresh(); else setLoading(false); }, [supported, refresh]);

  const enable = async () => {
    setBusy(true);
    try {
      await subscribe();
      toast.success("Push notifications enabled on this device");
      await refresh();
    } catch (e) {
      toast.error(e.message || "Could not enable push notifications");
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await unsubscribe();
      toast.success("Push notifications disabled on this device");
      await refresh();
    } catch (e) {
      toast.error(e.message || "Could not disable");
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/push/test");
      toast.success(`Test sent to ${data.sent} device${data.sent === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Test push failed");
    } finally {
      setBusy(false);
    }
  };

  // Detect iOS Safari (installed vs not) — iOS needs standalone mode for push.
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const needsInstall = isIOS && !standalone;

  return (
    <div className="bg-white border border-gray-200 p-6" data-testid="push-settings-card">
      <div className="flex items-start gap-4">
        <div className={`w-10 h-10 flex items-center justify-center flex-shrink-0 ${
          enabled ? "bg-orange-500 text-white" : "bg-gray-100 text-gray-500"
        }`}>
          {enabled ? <Bell className="w-5 h-5" strokeWidth={2.5} /> : <BellOff className="w-5 h-5" strokeWidth={2} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="label-caps">Notifications</div>
          <div className="font-display font-bold text-xl tracking-tight">Push Notifications</div>
          <p className="text-sm text-gray-600 mt-1">
            Real-time alerts for load status changes, exceptions, and hauler mismatches — delivered to your device even when the app is closed.
          </p>

          {loading && (
            <div className="mt-4 flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Checking status…
            </div>
          )}

          {!loading && !supported && (
            <div className="mt-4 flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 p-3">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                This browser does not support push notifications. Try Chrome, Edge, Firefox, or Safari (iOS 16.4+).
              </div>
            </div>
          )}

          {!loading && supported && needsInstall && !enabled && (
            <div className="mt-4 flex items-start gap-2 text-sm text-blue-900 bg-blue-50 border border-blue-200 p-3">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                On iPhone/iPad you must first <strong>Add SoilHaul to Home Screen</strong> (Safari share menu) and open it from there. Push notifications only work inside the installed app.
              </div>
            </div>
          )}

          {!loading && supported && !needsInstall && !enabled && (
            <button
              onClick={enable}
              disabled={busy}
              data-testid="push-enable-button"
              className="mt-4 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-semibold uppercase tracking-widest text-xs px-4 py-2.5 transition-colors flex items-center gap-2"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
              Enable notifications
            </button>
          )}

          {!loading && enabled && (
            <div className="mt-4 space-y-3">
              <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 px-3 py-2">
                <CheckCircle2 className="w-4 h-4" />
                <span>
                  Enabled on <strong>this device</strong>
                  {count > 1 && <> (and {count - 1} other{count - 1 === 1 ? "" : "s"})</>}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={sendTest}
                  disabled={busy}
                  data-testid="push-test-button"
                  className="bg-gray-900 hover:bg-gray-800 disabled:opacity-50 text-white font-semibold uppercase tracking-widest text-xs px-4 py-2 transition-colors flex items-center gap-2"
                >
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bell className="w-3.5 h-3.5" />}
                  Send test
                </button>
                <button
                  onClick={disable}
                  disabled={busy}
                  data-testid="push-disable-button"
                  className="text-gray-600 hover:text-red-600 font-semibold uppercase tracking-widest text-xs px-3 py-2 transition-colors"
                >
                  Disable on this device
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
